import type { Client } from "@sentry/types";
import { Scope } from "@sentry/core";
import type { showReportDialog } from "@sentry/browser";
import {
  logLevels,
  type RfcLogLevelNumber,
  severities,
} from "~/logging/levels";

interface LogArguments {
  level: RfcLogLevelNumber;
  message: string;
  error?: unknown;
  extra?: Record<string, unknown>;
}

export type LogFunction = (args: LogArguments) => void;

/**
 * Log a message and/or error.
 *
 * To prevent infinite loops with error logging, this function MUST NOT throw any error.
 *
 * @param level
 *   An integer representing a valid RFC log level.
 * @param message
 *   The message to log.
 * @param error
 *   The error to log.
 * @param extra
 *   Any extra data, which will be converted to JSON and appended.
 *   Useful for logging things like the component state at the time of the error.
 * @param sentryClient
 *   The Sentry client to use for logging the error.
 *   If not given, the error will be logged to the console instead.
 * @param promptForUserFeedback
 *   A function to call after reporting the error to Sentry that will ask the user for more info.
 *   If not given, no user feedback will be prompted.
 *   Ignored if no Sentry client is given.
 *   See https://docs.sentry.io/platforms/javascript/enriching-events/user-feedback
 */
export function log({
  level,
  message,
  error,
  extra,
  sentryClient,
  promptForUserFeedback,
}: LogArguments & {
  sentryClient?: Client;
  promptForUserFeedback?: typeof showReportDialog;
}): void {
  if (sentryClient) {
    try {
      const eventId = logToSentry({
        sentryClient,
        level,
        message,
        error,
        extra,
      });
      if (eventId && promptForUserFeedback) {
        promptForUserFeedback({ eventId });
      }
      return;
    } catch (e) {
      // Fall through to console logging.
    }
  }

  try {
    return logToConsole({ level, message, error, extra });
  } catch (e) {
    // Ignore.
  }
}

function logToSentry({
  sentryClient,
  level,
  message,
  error,
  extra,
}: LogArguments & {
  sentryClient: Client;
}): string | null {
  const sentryLevel = severities[level];
  const scope = new Scope();
  if (sentryLevel) {
    scope.setLevel(sentryLevel);
  }
  if (extra) {
    scope.setExtras(extra);
  }

  if (error) {
    scope.setExtra("message", message);
    return sentryClient.captureException(error, undefined, scope) ?? null;
  } else {
    return (
      sentryClient.captureMessage(message, sentryLevel, undefined, scope) ??
      null
    );
  }
}

function logToConsole({ level, message, error, extra }: LogArguments): void {
  let f;
  if (level <= logLevels.error) {
    f = console.error;
  } else if (level <= logLevels.notice) {
    f = console.warn;
  } else if (level <= logLevels.info) {
    f = console.info;
  } else if (level <= logLevels.debug) {
    f = console.debug;
  } else {
    f = console.log;
  }

  console.group(message);
  if (error) {
    f(error);
  }
  if (extra) {
    f(extra);
  }
  console.groupEnd();
}
